// src/contexts/AuthContext.js
// Context global pour l'authentification

import React, { createContext, useState, useEffect, useContext } from 'react';
import authService from '../services/authService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth doit être utilisé dans un AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [needsSecretCodeSetup, setNeedsSecretCodeSetup] = useState(false);

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('token');

      if (token) {
        const result = await authService.getProfile();
        if (result.success) {
          setUser(result.data);
        } else {
          authService.logout();
        }
      }

      setLoading(false);
    };

    initAuth();
  }, []);

  const login = async (phone, credential, usesSecretCode = true) => {
    const result = await authService.login(phone, credential, usesSecretCode);

    if (result.success) {
      setUser(result.data.user);
      setNeedsSecretCodeSetup(!!result.mustSetSecretCode);
    }

    return result;
  };

  const register = async (userData) => {
    const result = await authService.register(userData);

    if (result.success) {
      setUser(result.data.user);
      setNeedsSecretCodeSetup(false);
    }

    return result;
  };

  const completeSecretCodeSetup = async (secretCode, confirmSecretCode) => {
    const result = await authService.setupSecretCode(secretCode, confirmSecretCode);
    if (result.success) {
      setNeedsSecretCodeSetup(false);
      if (result.data.user) setUser(result.data.user);
    }
    return result;
  };

  const logout = () => {
    authService.logout();
    setUser(null);
    setNeedsSecretCodeSetup(false);
  };

  const updateUserData = (data) => {
    if (!data) return;
    setUser((prev) => ({ ...(prev || {}), ...data }));
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    updateUserData,
    completeSecretCodeSetup,
    needsSecretCodeSetup,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
